import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  UnfoldLess as UnfoldLessIcon,
  UnfoldMore as UnfoldMoreIcon,
} from '@mui/icons-material';
import { Box, Collapse, Tooltip, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useCallback, useState } from 'react';
import { BasicFiltersWrapper } from '../BasicFiltersWrapper';
import { BasicFilters } from '../Filters';
import { useFilterSetsContext } from './FilterBarWrapper.context';

const useStyles = makeStyles(theme => ({
  filterBarWrapper: {
    position: 'relative',
    display: 'grid',
    gap: '0.75rem',
    padding: '0.5rem 1rem',
    borderBottom: `solid ${theme.palette.baseColors.black.c12} thin`,

    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: '1fr auto',
      alignItems: 'center',
    },
  },
  advancedLabel: {
    position: 'absolute',
    bottom: '0',
    left: '50%',
    fontSize: '11px',
    transform: 'translate(-50%, 50%)',
    backgroundColor: 'white',
    padding: '0 0.25em',
    height: '16px',
    lineHeight: '16px',
  },
  filters: {
    display: 'flex',
    gap: '0.75rem',
    [theme.breakpoints.down('sm')]: {
      display: 'grid',
      gridTemplateColumns: 'auto 1fr auto',
    },
  },

  controls: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '55px',
  },
  button: { padding: 0 },
  icon: {
    fontSize: '.7rem',
    transform: 'scale(2.5)',
  },
  label: {
    fontSize: '12px',
    lineHeight: '19px',
  },
  controlsWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  filterItemClass: {
    minWidth: '1px',
  },
  withSeenFilters: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  minimized: {
    [theme.breakpoints.down('sm')]: {
      height: '50px',
      overflowY: 'clip',
    },
  },
  minimizeButtonWrapper: {
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  minimizeButton: {
    marginTop: '0.5rem',
  },
}));

const Actions = ({ expandToSeeFiltersSelected, children }) => {
  const style = useStyles();

  if (!expandToSeeFiltersSelected) {
    return <>{children}</>;
  }

  return <div className={style.withSeenFilters}>{children}</div>;
};

export const FilterBarWrapper = ({
  children,
  growFirstFilter,
  filters,
  applyFilters,
  basicFilterTypes,
  context,
  googleAnalyticsTag,
  advancedFiltersOpened,
  advancedFilters,
  expandToSeeFiltersSelected,
}) => {
  const [minimize, setMinimize] = useState(false);

  const { currentFilterSetIndex, nextFilterSet, previousFilterSet, filterSetsCount } =
    useFilterSetsContext();

  const minimizeMaximize = useCallback(() => setMinimize(v => !v), []);

  const refresh = useCallback(
    () => setTimeout(() => window.dispatchEvent(new Event('resize')), 400),
    [],
  );

  const classes = useStyles();

  return (
    <>
      <div className={classes.filterBarWrapper}>
        <div className={classes.filters}>
          <div className={classes.minimizeButtonWrapper}>
            <Tooltip title={`${minimize ? 'Expand' : 'Collapse'} filter bar`}>
              <IconButton
                className={classes.minimizeButton}
                onClick={minimizeMaximize}
                size='large'
              >
                {minimize ? <UnfoldMoreIcon /> : <UnfoldLessIcon />}
              </IconButton>
            </Tooltip>
          </div>
          {!!filters && (
            <BasicFiltersWrapper
              className={clsx({
                [classes.minimized]: minimize,
              })}
              numberOfFields={basicFilterTypes?.length}
              growFirstFilter={growFirstFilter}
            >
              <BasicFilters
                filters={filters}
                applyFilters={applyFilters}
                basicFilterTypes={basicFilterTypes}
                context={context}
                googleAnalyticsTag={googleAnalyticsTag}
                filterItemClass={classes.filterItemClass}
              />
            </BasicFiltersWrapper>
          )}
          {filterSetsCount > 1 && (
            <div className={classes.controlsWrapper}>
              <Tooltip title='Move between your filter sets.'>
                <div className={classes.controls}>
                  <IconButton
                    className={classes.button}
                    onClick={previousFilterSet}
                    disabled={currentFilterSetIndex === 0}
                    size='large'
                  >
                    <ExpandLessIcon className={classes.icon} />
                  </IconButton>
                  <Typography className={classes.label} variant='caption'>
                    {currentFilterSetIndex + 1}/{filterSetsCount}
                  </Typography>
                  <IconButton
                    className={classes.button}
                    onClick={nextFilterSet}
                    disabled={currentFilterSetIndex === filterSetsCount - 1}
                    size='large'
                  >
                    <ExpandMoreIcon className={classes.icon} />
                  </IconButton>
                </div>
              </Tooltip>
            </div>
          )}
        </div>
        <Actions expandToSeeFiltersSelected={expandToSeeFiltersSelected}>{children}</Actions>

        {advancedFiltersOpened && (
          <Typography className={classes.advancedLabel} variant='overline'>
            Advanced Filters
          </Typography>
        )}
      </div>
      {!!advancedFilters && (
        <Collapse
          id='advanced-filters-panel'
          in={advancedFiltersOpened}
          timeout='auto'
          unmountOnExit
          addEndListener={refresh}
          sx={{ marginLeft: '1rem', flexShrink: 0 }}
        >
          <Box sx={{ marginTop: '1rem' }} />
          {advancedFilters}
        </Collapse>
      )}
    </>
  );
};
